import { createTheme } from '@mui/material';
const baseColors = {
  atomic: '#3F4D55',
  blueWhale: '#002F49',
  black: '#000000',
  emerald: '#3AD47C',
  ghostWhite: '#F1F1F3',
  gainsboro: '#E1E1E1',
  grey: '#7f7f7f',
  matterhorn: '#565656',
  pelorous: '#2388C1',
  red: '#EC221F',
  white: '#FFFFFF',
  destructiveColor: '#FF0000',
  prussianBlue: '#0C3351',
  chineseBlack: '#0D151B',
  charlestonGreen: '#2C2C2C',
  lightSilver: '#D9D9D9',
  phSilver: '#b3b3b3',
  cultured: '#F5F5F5',
  oldSilver: '#828587',
  japaneseLaurel: '#347E34',
  amaranth: '#E72455',
  mantis: '#63BE63',
  maximumYellowRed: '#F3BC58',
  skylineBlue: '#1E88E5',
  dimGray: '#757575',
  darkCerulean: '#124E7C',
  badgeBackground: '#eff8ef',
};

const appBaseColors = {
  appBar: baseColors.blueWhale,
  primary: baseColors.prussianBlue,
  secondary: baseColors.emerald,
};

const componentStateColors = {
  activeNavLink: baseColors.blueWhale,
  signInButtonBackground: baseColors.emerald,
  signInFormBackground: baseColors.blueWhale,
  inputBackground: baseColors.gainsboro,
  inputLabel: baseColors.pelorous,
  inputValid: baseColors.emerald,
  inputInvalid: baseColors.red,
};

export const Color = {
  ...baseColors,
  ...appBaseColors,
  ...componentStateColors,
};

/**
 * The floating label color bug as of today:
 * https://bitbucket.org/isbx/boilerplates-cms/pull-requests/2/feature-bplt-66-cms-set-up-theming/diff#comment-102555670
 * https://github.com/material-components/material-components-web/issues/2718
 */

export const GlobalThemeStyle = {
  html: {
    boxSizing: 'border-box',
  },

  body: {
    margin: 0,
    fontFamily: "'Poppins', sans-serif",
  },

  'html, body': {
    height: '100%',
  },

  '*, *:before, *:after': {
    boxSizing: 'border-box',
  },
};

const generatePalette = (colors: { [key: string]: string }) => {
  return Object.keys(colors).reduce((curr, key) => {
    return {
      ...curr,
      [key]: {
        main: colors[key],
      },
    };
  }, {});
};

export type Theme = Readonly<typeof theme>;

export const theme = createTheme({
  palette: generatePalette(Color),
  typography: {
    fontFamily: "'Poppins', sans-serif",
    h5: {
      fontWeight: 700,
      fontSize: 24,
      color: baseColors.chineseBlack,
    },
    subtitle1: {
      fontSize: 14,
      fontWeight: 600,
      color: baseColors.oldSilver,
    },
    body2: {
      color: baseColors.charlestonGreen,
      fontSize: 16,
    },
  },
  components: {
    MuiDialogActions: {
      styleOverrides: {
        root: {
          button: {
            '&.MuiButton-colorPrimary': {
              color: baseColors.white,
            },
            '&.MuiButton-outlinedPrimary': {
              color: baseColors.prussianBlue,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: Color.white,
          borderRadius: 10,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: Color.chineseBlack,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: Color.chineseBlack,
            borderWidth: '2px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: Color.amaranth,
            borderWidth: '2px',
          },
        },
        notchedOutline: {
          borderColor: Color.lightSilver,
        },
        input: {
          paddingLeft: 20,
          paddingRight: 20,
        },
        adornedStart: {
          paddingLeft: 16,
          input: {
            paddingLeft: 8,
          },
        },
        adornedEnd: {
          paddingRight: 16,
        },
        multiline: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: baseColors.prussianBlue,
          fontWeight: 400,
          height: 48,
          borderRadius: 8,
          textTransform: 'none',
          '&.Mui-disabled': {
            backgroundColor: baseColors.lightSilver,
            color: baseColors.phSilver,
          },
          '&.MuiButton-containedPrimary .MuiButton-startIcon svg, &.MuiButton-containedPrimary .MuiButton-endIcon svg': {
            color: baseColors.white,
          },
        },
        sizeMedium: {
          height: 56,
        },
        sizeSmall: {
          height: 48,
        },
        outlined: {
          background: 'none',
          borderColor: baseColors.prussianBlue,
          color: baseColors.prussianBlue,
          borderWidth: '1px',
          fontSize: '16px',
          fontWeight: 500,
          borderRadius: 8,
          '&:hover': {
            borderColor: baseColors.prussianBlue,
          },
        },
        text: {
          background: 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: Color.cultured,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: 3.5,
        },
        head: {
          backgroundColor: baseColors.cultured,
          paddingTop: 0,
          paddingBottom: 0,
          color: Color.oldSilver,
          height: 37,
        },
        body: {
          fontSize: '14px',
          color: Color.chineseBlack,
          borderBottom: `1px solid ${Color.lightSilver}`,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: baseColors.chineseBlack,
        },
        fontSizeLarge: {
          fontSize: 80,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          color: baseColors.white,
          svg: {
            color: baseColors.white,
          },
        },
        standardSuccess: {
          backgroundColor: baseColors.mantis,
        },
        standardError: {
          backgroundColor: baseColors.amaranth,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: 14,
          height: 20,
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
    },
  },
});
